import { useState } from 'react';
import { Search, Globe, Building2, CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface BinInfo {
  scheme?: string;
  type?: string;
  brand?: string;
  prepaid?: boolean;
  country?: {
    name?: string;
    emoji?: string;
    alpha2?: string;
  };
  bank?: {
    name?: string;
    url?: string;
    phone?: string;
    city?: string;
  };
}

const BinLookup = () => {
  const [bin, setBin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<BinInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lookupBin = async () => {
    const cleanBin = bin.replace(/\D/g, '');
    
    if (!cleanBin || cleanBin.length < 6 || cleanBin.length > 8) {
      toast.error('Please enter a valid BIN (6-8 digits)');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`https://lookup.binlist.net/${cleanBin}`, {
        headers: {
          'Accept-Version': '3',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('BIN not found in database');
        }
        throw new Error('Failed to lookup BIN');
      }
      
      const data = await response.json();
      setResult(data);
      toast.success('BIN information retrieved');
    } catch (err: any) {
      setError(err.message || 'Failed to lookup BIN');
      toast.error(err.message || 'Failed to lookup BIN');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBinInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setBin(value);
    setResult(null);
    setError(null);
  };

  return (
    <Card className="glass border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5 text-primary" />
          BIN Lookup
        </CardTitle>
        <CardDescription>
          Look up bank and card information from BIN numbers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="bin-lookup">BIN Number (First 6-8 digits)</Label>
          <Input
            id="bin-lookup"
            placeholder="e.g., 424242"
            value={bin}
            onChange={handleBinInput}
            maxLength={8}
            className="font-mono"
          />
        </div>

        <Button onClick={lookupBin} disabled={isLoading} className="w-full gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Lookup BIN
        </Button>

        {error && (
          <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/10 text-destructive animate-fade-in">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="space-y-4 animate-fade-in">
            {/* Card Info */}
            <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                Card Information
              </h3>
              <div className="grid gap-2">
                {result.scheme && (
                  <div className="flex items-center justify-between p-2 rounded bg-background/50">
                    <span className="text-muted-foreground">Scheme</span>
                    <Badge variant="secondary" className="capitalize">
                      {result.scheme}
                    </Badge>
                  </div>
                )}
                {result.type && (
                  <div className="flex items-center justify-between p-2 rounded bg-background/50">
                    <span className="text-muted-foreground">Type</span>
                    <Badge variant="outline" className="capitalize">
                      {result.type}
                    </Badge>
                  </div>
                )}
                {result.brand && (
                  <div className="flex items-center justify-between p-2 rounded bg-background/50">
                    <span className="text-muted-foreground">Brand</span>
                    <span className="capitalize">{result.brand}</span>
                  </div>
                )}
                {result.prepaid !== undefined && (
                  <div className="flex items-center justify-between p-2 rounded bg-background/50">
                    <span className="text-muted-foreground">Prepaid</span>
                    <Badge variant={result.prepaid ? 'default' : 'secondary'}>
                      {result.prepaid ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Country Info */}
            {result.country && (
              <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Globe className="h-4 w-4 text-primary" />
                  Country
                </h3>
                <div className="grid gap-2">
                  {result.country.name && (
                    <div className="flex items-center justify-between p-2 rounded bg-background/50">
                      <span className="text-muted-foreground">Country</span>
                      <span className="flex items-center gap-2">
                        {result.country.emoji && <span>{result.country.emoji}</span>}
                        {result.country.name}
                        {result.country.alpha2 && (
                          <Badge variant="outline" className="text-xs">
                            {result.country.alpha2}
                          </Badge>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bank Info */}
            {result.bank && (result.bank.name || result.bank.city) && (
              <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  Issuing Bank
                </h3>
                <div className="grid gap-2">
                  {result.bank.name && (
                    <div className="flex items-center justify-between p-2 rounded bg-background/50">
                      <span className="text-muted-foreground">Bank Name</span>
                      <span className="text-right">{result.bank.name}</span>
                    </div>
                  )}
                  {result.bank.city && (
                    <div className="flex items-center justify-between p-2 rounded bg-background/50">
                      <span className="text-muted-foreground">City</span>
                      <span>{result.bank.city}</span>
                    </div>
                  )}
                  {result.bank.url && (
                    <div className="flex items-center justify-between p-2 rounded bg-background/50">
                      <span className="text-muted-foreground">Website</span>
                      <a
                        href={`https://${result.bank.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {result.bank.url}
                      </a>
                    </div>
                  )}
                  {result.bank.phone && (
                    <div className="flex items-center justify-between p-2 rounded bg-background/50">
                      <span className="text-muted-foreground">Phone</span>
                      <span>{result.bank.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BinLookup;
